export interface StateManagerConfig {
    receiverTarget: string;
    eventThatAssigns: string;
    eventThatResets?: string;
    propagatesToOtherAssigners?: boolean;
    defaultState?: string;
}

export default class StateManager {
    private static readonly ASSIGNER_ATTRIBUTE = 'data-state-manager-assigner';
    private static readonly ASSIGNER_IGNORE_ATTRIBUTE = 'data-state-manager-assigner-ignore';
    private static readonly ASSIGNER_ACTIVE_ATTRIBUTE = 'data-state-manager-assigner-active';
    private static readonly RECEIVER_ATTRIBUTE = 'data-state-manager-receiver';
    private static readonly RECEIVER_STATE_ATTRIBUTE = 'data-state-manager-receiver-state';

    public static readonly STATE_NOT_SET_ERR = 'data-state-manager-assigner state not set';
    public static readonly RECEIVER_NOT_FOUND_ERR = 'receiver not found';

    private propagatesToOtherAssigners: boolean;
    private receiverTarget: string;
    private defaultState?: string;

    constructor(config: StateManagerConfig) {
        this.receiverTarget = config.receiverTarget;
        this.propagatesToOtherAssigners = config.propagatesToOtherAssigners || false;
        this.defaultState = config.defaultState;

        this.observeAssigners(config.eventThatAssigns);

        if (config.eventThatResets) {
            this.addResetStateEventListener(config.eventThatResets);
        }

        if (this.defaultState) {
            this.propagateState(this.defaultState);
        }
    }

    public propagateStateForElement(element: Element) {
        const state = this.getAssignersState(element);
        this.propagateState(state);
    }

    private getAssigners(): HTMLElement[] {
        let assigners: HTMLElement[] = [];
        this.getReceivers().forEach(receiver => {
            assigners = assigners.concat(
                Array.from(
                    receiver.querySelectorAll<HTMLElement>(`[${StateManager.ASSIGNER_ATTRIBUTE}]`)
                )
            );
        });
        return assigners;
    }

    private getReceivers(): HTMLElement[] {
        const receivers = document.querySelectorAll<HTMLElement>(
            `[${StateManager.RECEIVER_ATTRIBUTE}="${this.receiverTarget}"]`
        );
        if (!receivers) {
            throw new Error(StateManager.RECEIVER_NOT_FOUND_ERR);
        }
        return Array.from(receivers);
    }

    private observeAssigners(eventType: string) {
        this.getAssigners().forEach(assigner => {
            assigner.addEventListener(eventType, () => {
                this.propagateStateForElement(assigner);
            });
        });
    }

    private getAssignersState(assigner: Element): string {
        const state = assigner.getAttribute(StateManager.ASSIGNER_ATTRIBUTE);
        if (!state) {
            throw new Error(StateManager.STATE_NOT_SET_ERR);
        }
        return state;
    }

    private propagateState(state: string) {
        this.getReceivers().forEach(receiver => {
            if (!this.propagatesToOtherAssigners) {
                this.preventOtherAssignersFromReceivingState(state);
            }
            receiver.setAttribute(StateManager.RECEIVER_STATE_ATTRIBUTE, state);
        });

        this.getAssigners().forEach(assigner => {
            if (this.getAssignersState(assigner) === state) {
                assigner.setAttribute(StateManager.ASSIGNER_ACTIVE_ATTRIBUTE, 'true');
            } else {
                assigner.setAttribute(StateManager.ASSIGNER_ACTIVE_ATTRIBUTE, 'false');
            }
        });
    }

    private addResetStateEventListener(eventType: string) {
        this.getAssigners().forEach(assigner => {
            assigner.addEventListener(eventType, () => {
                this.resetReceivers();
            });
        });
    }

    private resetReceivers() {
        this.getReceivers().forEach(this.resetElementState);
        if (this.defaultState) {
            this.propagateState(this.defaultState);
        }
    }

    private resetElementState(element: HTMLElement) {
        element.setAttribute(StateManager.RECEIVER_STATE_ATTRIBUTE, '');
    }

    private preventOtherAssignersFromReceivingState(state: string) {
        this.getAssigners().forEach(assigner => {
            if (this.getAssignersState(assigner) !== state) {
                assigner.setAttribute(StateManager.ASSIGNER_IGNORE_ATTRIBUTE, 'true');
            } else {
                assigner.setAttribute(StateManager.ASSIGNER_IGNORE_ATTRIBUTE, 'false');
            }
        });
    }
}
